import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  List,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { tasksAPI } from '../services/api';

function MyWorkPage() {
  const [myWork, setMyWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMyWork();
  }, []);

  const loadMyWork = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await tasksAPI.getMyWork();
      setMyWork(response.data);
    } catch (err) {
      setError('Failed to load your tasks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderTaskList = (tasks, emptyMessage) => {
    if (tasks.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <List sx={{ pt: 0 }}>
        {tasks.map((task, index) => (
          <React.Fragment key={task.id}>
            {index > 0 && <Divider />}
            <ListItemButton sx={{ py: 2 }}>
              <ListItemText
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {task.name}
                    </Typography>
                    <Chip
                      label={task.priority}
                      size="small"
                      color={getPriorityColor(task.priority)}
                      sx={{ height: 20 }}
                    />
                    {task.effort_estimate && (
                      <Chip
                        label={`${task.effort_estimate} pts`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {task.project_name}
                    </Typography>
                    {task.status && (
                      <Chip
                        label={task.status.name}
                        size="small"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: task.status.color,
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                }
              />
              {task.tags && task.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 2 }}>
                  {task.tags.slice(0, 3).map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor: tag.color,
                        color: 'white',
                      }}
                    />
                  ))}
                </Box>
              )}
            </ListItemButton>
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Work
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All tasks assigned to you
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* My Work Sections */}
      {!loading && myWork && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" color="error.main">
                {myWork.counts.overdue}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overdue
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" color="warning.main">
                {myWork.counts.today}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Due Today
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" color="info.main">
                {myWork.counts.this_week}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This Week
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" color="primary.main">
                {myWork.counts.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Tasks
              </Typography>
            </Paper>
          </Box>

          {/* Overdue Tasks */}
          <Accordion defaultExpanded={myWork.overdue.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="error" />
                <Typography variant="h6">Overdue</Typography>
                <Badge badgeContent={myWork.counts.overdue} color="error" />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderTaskList(myWork.overdue, 'No overdue tasks! 🎉')}
            </AccordionDetails>
          </Accordion>

          {/* Today */}
          <Accordion defaultExpanded={myWork.today.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TodayIcon color="warning" />
                <Typography variant="h6">Due Today</Typography>
                <Badge badgeContent={myWork.counts.today} color="warning" />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderTaskList(myWork.today, 'No tasks due today')}
            </AccordionDetails>
          </Accordion>

          {/* This Week */}
          <Accordion defaultExpanded={myWork.this_week.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRangeIcon color="info" />
                <Typography variant="h6">This Week</Typography>
                <Badge badgeContent={myWork.counts.this_week} color="info" />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderTaskList(myWork.this_week, 'No tasks due this week')}
            </AccordionDetails>
          </Accordion>

          {/* Later */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon color="primary" />
                <Typography variant="h6">Later</Typography>
                <Badge badgeContent={myWork.counts.later} color="primary" />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderTaskList(myWork.later, 'No tasks scheduled for later')}
            </AccordionDetails>
          </Accordion>

          {/* No Due Date */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon />
                <Typography variant="h6">No Due Date</Typography>
                <Badge badgeContent={myWork.counts.no_due_date} color="default" />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderTaskList(myWork.no_due_date, 'No tasks without due date')}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Empty State */}
      {!loading && !myWork && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No tasks assigned to you
          </Typography>
        </Paper>
      )}
    </Container>
  );
}

export default MyWorkPage;
